// Replacement for the generated `attachSupabaseAuth` middleware.
//
// The generated middleware calls `supabase.auth.getSession()` and forwards
// whatever it returns. On a fresh admin page load the persisted session may
// already be past its `expires_at`; getSession returns the stale token
// verbatim (it does NOT auto-refresh), the server-side `getClaims` rejects
// it as "Invalid token", and the panel surfaces "Try again". A single
// manual click then succeeds because supabase-js has quietly refreshed
// the token in the background between the two clicks.
//
// This middleware fixes the race at the root: if the persisted session is
// missing or within 30s of expiry, force a refresh (via getUser, which
// auto-refreshes and awaits the new token) before attaching the header.
import { createMiddleware } from "@tanstack/react-start";

const REFRESH_SKEW_SEC = 5;
const AUTH_TIMEOUT_MS = 4_000;

let inFlightToken: Promise<string | undefined> | null = null;

function withTimeout<T>(promise: Promise<T>, ms: number): Promise<T | undefined> {
  return Promise.race([
    promise,
    new Promise<undefined>((resolve) => window.setTimeout(() => resolve(undefined), ms)),
  ]);
}

async function getFreshAccessToken(): Promise<string | undefined> {
  // TanStack's `.client()` middleware can run during SSR. Do not touch the
  // browser auth client there; protected routes make their first real calls
  // after hydration, when localStorage is available.
  if (typeof window === "undefined") return undefined;

  if (inFlightToken) return inFlightToken;

  inFlightToken = getFreshAccessTokenInner().finally(() => {
    inFlightToken = null;
  });
  return inFlightToken;
}

async function getFreshAccessTokenInner(): Promise<string | undefined> {
  const { supabase } = await import("./client");
  const { data: sessionData } = await supabase.auth.getSession();
  const session = sessionData.session;
  const now = Math.floor(Date.now() / 1000);
  const needsRefresh =
    !session?.access_token ||
    (typeof session.expires_at === "number" && session.expires_at - now < REFRESH_SKEW_SEC);

  if (!needsRefresh) return session!.access_token;

  // refreshSession is the narrowest fix: one auth refresh, shared across
  // concurrent admin queries, with a hard timeout so panels don't sit on an
  // endless skeleton if auth is temporarily slow.
  await withTimeout(supabase.auth.refreshSession(), AUTH_TIMEOUT_MS);
  const { data: refreshed } = await supabase.auth.getSession();
  return refreshed.session?.access_token;
}

export const attachSupabaseAuth = createMiddleware({ type: "function" }).client(
  async ({ next }) => {
    const token = await getFreshAccessToken();
    return next({
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });
  },
);
